import { apiClient } from "./client";
import { Project } from "./projects";
import { User } from "./auth";
import { Did } from "@/types";

export interface Planning {
  id: Did;
  user_id: Did;
  project_id: Did;
  date: string;
  start_time: string;
  end_time: string;
  task_description?: string;
  note?: string;
  created_at: string;
  updated_at: string;
  users?: User[];
  project?: Project;
}

/**
 * Get planning for a specific date (defaults to today)
 */
export const getPlanning = async (
  date?: string | Date
): Promise<Planning[]> => {
  if (!date) {
    date = new Date();
  }

  const dateString = typeof date === "string" ? date : databaseTimeFormat(date);

  const { data } = await apiClient.get<Planning[]>("/planning", {
    params: { from: dateString, to: dateString },
  });

  return data;
};

export const getFirstDayOfTheWeek = (date: Date) => {
  const monday = new Date(date);
  const dow = date.getDay();
  const subtract = dow === 0 ? 6 : dow - 1; // Sunday === 0, go one week back

  monday.setDate(date.getDate() - subtract);
  return monday;
};

export const getLastDayOfTheWeek = (date: Date) => {
  const sunday = new Date(date);
  const dow = date.getDay();
  const add = dow === 0 ? 0 : 7 - dow; // Sunday === 0, is today

  sunday.setDate(date.getDate() + add);
  return sunday;
};

export const databaseTimeFormat = (date: Date) => {
  return date.toISOString().split("T")[0];
};

/**
 * Get planning for current week
 */
export const getWeekPlanning = async (
  date?: Date | string
): Promise<Planning[]> => {
  if (!date) {
    date = new Date();
  }

  if (typeof date === "string") {
    date = new Date(date);
  }

  const monday = getFirstDayOfTheWeek(date);
  const sunday = getLastDayOfTheWeek(date);

  const { data } = await apiClient.get<Planning[]>("/planning", {
    params: {
      from: databaseTimeFormat(monday),
      to: databaseTimeFormat(sunday),
    },
  });
  return data;
};

/**
 * Get my planning (authenticated user only)
 */
export const getMyPlanning = async (
  date?: string | Date
): Promise<Planning[]> => {
  if (!date) {
    date = new Date();
  }

  const dateString = typeof date === "string" ? date : databaseTimeFormat(date);

  const { data } = await apiClient.get<{ data: Planning[] }>("/planning/my", {
    params: {
      from: dateString,
      to: dateString,
    },
  });
  return data.data;
};
